import React from "react";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { FileText } from "lucide-react";
import { BillActionDropdown } from "./BillActionDropdown";
import { BillImpactCell } from "./BillImpactCell";
import { PrintAIReviewButton } from "./PrintAIReviewButton";
import { Bill, BillAction } from "@/types/bill";

interface BillCardProps {
  bill: Bill;
  isSelected: boolean;
  sessionCode: string;
  onSelectBill: (billId: string) => void;
  onProcessAction: (billId: string, action: BillAction) => void;
  onShowImpactDetails?: (bill: Bill) => void;
  handlePDFClick: (url: string, e: React.MouseEvent) => void;
  processingActions?: Record<string, BillAction>;
}

export function BillCard({
  bill,
  isSelected,
  onSelectBill,
  onProcessAction,
  onShowImpactDetails,
  handlePDFClick,
  processingActions,
}: BillCardProps) {
  const formatDate = (dateString?: string) => {
    return dateString ? new Date(dateString).toLocaleString("en-US", {
      timeZone: "America/New_York",
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }) : "";
  };

  return (
    <Card className={`overflow-hidden ${isSelected ? 'ring-2 ring-primary' : ''}`}>
      <CardContent className="p-4 space-y-3">
        <div className="flex justify-between items-start">
          <div className="flex items-center gap-2">
            <span className="font-medium">{bill.billNumber}</span>
          </div>

          <div className="flex gap-1">
            {bill.url && bill.fullTextAvailable && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={(e) => handlePDFClick(bill.url, e)}
                      className="px-2 w-8 h-8"
                    >
                      <FileText className="h-4 w-4" />
                      <span className="sr-only">View Bill PDF</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    View Bill PDF
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </div>

        <div>
          <p className="text-sm font-medium text-muted-foreground">Title</p>
          <p className="text-sm line-clamp-2">{bill.title || 'No Title'}</p>
        </div>

        <div className="flex justify-between items-center">
          <div>
            <p className="text-sm font-medium text-muted-foreground">Impact</p>
            <BillImpactCell
              analysis={bill.monitor_analysis}
              onClick={() => onShowImpactDetails?.(bill)}
            />
          </div>

          {bill.crossfileBillNumber && (
            <div>
              <p className="text-sm font-medium text-muted-foreground">Crossfile</p>
              <Badge variant="outline">{bill.crossfileBillNumber}</Badge>
            </div>
          )}
        </div>

        <div>
          <p className="text-sm font-medium text-muted-foreground">Modified</p>
          <p className="text-sm">{formatDate(bill.lastModified)}</p>
        </div>

        {bill.versions && bill.versions.length > 0 && (
          <div className="flex gap-2">
            <PrintAIReviewButton
              latestVersion={bill.versions[bill.versions.length - 1]}
              bill={bill}
              loadingAction={processingActions?.[bill.billNumber]}
            />
          </div>
        )}
      </CardContent>

      <CardFooter className="px-4 py-3 bg-muted/20 flex justify-between">
        <Button
          size="sm"
          variant={isSelected ? "default" : "outline"}
          onClick={() => onSelectBill(bill.billNumber)}
          className="px-3"
        >
          {isSelected ? "Selected" : "Select"}
        </Button>

        <div className="flex gap-2">
          <BillActionDropdown
            onAction={(action: BillAction) => onProcessAction(bill.billNumber, action)}
            disabled={!bill.fullTextAvailable || !!processingActions?.[bill.billNumber]}
          />
        </div>
      </CardFooter>
    </Card>
  );
}